import React, { useState, useEffect } from "react";
import SortBySelect from "../../dashboard-shared/sort-by-select/sort-by-select";
import Modal from "react-bootstrap/Modal";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import PropTypes from "prop-types";
import { FluentButton } from "../../../../components/FluentUI/Fluent-Button";
import FluentUiIcon from "../../../../assets/fluent-ui-icon";
import DateRangePicker from "../../../../components/date-range-picker/date-range-picker";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

const CompletedSTRsBarGraph = ({
  data,
  fetchData,
  customStart,
  customEnd,
  setCustomStart,
  setCustomEnd,
}) => {
  const storedSort = localStorage.getItem("completedSTRs_sort") || "Last 7 Days";
  const storedCategory = localStorage.getItem("completedSTRs_category") || "Trigger Type";
  const storedStart = localStorage.getItem("completedSTRs_start");
  const storedEnd = localStorage.getItem("completedSTRs_end");

  const [sort, setSort] = useState(storedSort);
  const [calendarShow, setCalendarShow] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(storedCategory);

  useEffect(() => {
    if (
      storedStart &&
      storedEnd &&
      storedStart !== "undefined" &&
      storedEnd !== "undefined"
    ) {
      const start = new Date(storedStart);
      const end = new Date(storedEnd);
      setCustomStart(start);
      setCustomEnd(end);
      fetchData(start, end);
    } else {
      handleSelect(storedSort);
    }
  }, []);

  const handleSelect = (selectedValue) => {
    let startDate = new Date();
    let endDate = new Date();

    if (selectedValue === "Last 7 Days") startDate.setDate(startDate.getDate() - 6);
    else if (selectedValue === "Last Month") startDate.setDate(startDate.getDate() - 30);
    else if (selectedValue === "Last 3 Months") startDate.setDate(startDate.getDate() - 90);
    else if (selectedValue === "Custom") {
      setCalendarShow(true);
      setSort("Custom");
      return;
    }

    setSort(selectedValue);
    setCustomStart(startDate);
    setCustomEnd(endDate);

    localStorage.setItem("completedSTRs_sort", selectedValue);
    localStorage.setItem("completedSTRs_start", startDate.toISOString());
    localStorage.setItem("completedSTRs_end", endDate.toISOString());

    fetchData(startDate, endDate);
  };

  const handleCalendarClose = () => {
    if (customStart && customEnd) {
      localStorage.setItem("completedSTRs_sort", "Custom");
      localStorage.setItem("completedSTRs_start", customStart.toISOString());
      localStorage.setItem("completedSTRs_end", customEnd.toISOString());
      fetchData(customStart, customEnd);
    }
    setCalendarShow(false);
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    localStorage.setItem("completedSTRs_category", value);
  };

  const complete = data?.Complete || {};
  const notFiling = data?.NotFiling || {};

  const categoryMappings = {
    "Trigger Type": {
      labels: [
        "Production Order",
        "High Risk Review",
        "Victim of Fraud",
        "Owner Behavior",
        "Transaction Monitoring",
        "Reoccurring STR",
      ],
      completeData: [
        complete.TriggerType?.ProductionOrderCount ?? 0,
        complete.TriggerType?.HighRiskCount ?? 0,
        complete.TriggerType?.VictimOfFraudCount ?? 0,
        complete.TriggerType?.OwnerBehaviorCount ?? 0,
        complete.TriggerType?.TransactionMonitoringCount ?? 0,
        complete.TriggerType?.ReoccuringSTRCount ?? 0,
      ],
      notFilingData: [
        notFiling.TriggerType?.ProductionOrderCount ?? 0,
        notFiling.TriggerType?.HighRiskCount ?? 0,
        notFiling.TriggerType?.VictimOfFraudCount ?? 0,
        notFiling.TriggerType?.OwnerBehaviorCount ?? 0,
        notFiling.TriggerType?.TransactionMonitoringCount ?? 0,
        notFiling.TriggerType?.ReoccuringSTRCount ?? 0,
      ],
    },
    Priority: {
      labels: ["Low", "Medium", "High", "Urgent"],
      completeData: [
        complete.Priority?.LowCount ?? 0,
        complete.Priority?.MediumCount ?? 0,
        complete.Priority?.HighCount ?? 0,
        complete.Priority?.UrgentCount ?? 0,
      ],
      notFilingData: [
        notFiling.Priority?.LowCount ?? 0,
        notFiling.Priority?.MediumCount ?? 0,
        notFiling.Priority?.HighCount ?? 0,
        notFiling.Priority?.UrgentCount ?? 0,
      ],
    },
    "Triggering Source": {
      labels: ["STR Alert Event", "Navaera Event", "Manual", "Other"],
      completeData: [
        complete.TriggerSource?.STRAlertEventCount ?? 0,
        complete.TriggerSource?.NavaeraEventCount ?? 0,
        complete.TriggerSource?.ManualCount ?? 0,
        complete.TriggerSource?.OtherCount ?? 0,
      ],
      notFilingData: [
        notFiling.TriggerSource?.STRAlertEventCount ?? 0,
        notFiling.TriggerSource?.NavaeraEventCount ?? 0,
        notFiling.TriggerSource?.ManualCount ?? 0,
        notFiling.TriggerSource?.OtherCount ?? 0,
      ],
    },
    Department: {
      labels: ["Branch", "Operational Risk", "Other", "N/A"],
      completeData: [
        complete.Department?.BranchCount ?? 0,
        complete.Department?.OperationalRiskCount ?? 0,
        complete.Department?.OtherCount ?? 0,
        complete.Department?.NACount ?? 0,
      ],
      notFilingData: [
        notFiling.Department?.BranchCount ?? 0,
        notFiling.Department?.OperationalRiskCount ?? 0,
        notFiling.Department?.OtherCount ?? 0,
        notFiling.Department?.NACount ?? 0,
      ],
    },
    "Branch Location": {
      labels:
        complete.BranchLocation?.Locations?.map((l) =>
          l.LocationName.replace(/([a-z])([A-Z])/g, "$1 $2")
        ) || [],
      completeData: complete.BranchLocation?.Locations?.map((l) => l.Count) || [],
      notFilingData: notFiling.BranchLocation?.Locations?.map((l) => l.Count) || [],
    },
  };

  const category = categoryMappings[selectedCategory];

  const chartData = {
    labels: category.labels,
    datasets: [
      {
        label: "Approved For Not Filing",
        data: category.notFilingData,
        backgroundColor: "#D4424F",
        borderColor: "#D4424F",
        borderWidth: 1,
        borderRadius: 4,
        categoryPercentage: 0.6,
        barPercentage: 0.7,
        maxBarThickness: 50,
        stack: "stack1",
      },
      {
        label: "Complete",
        data: category.completeData,
        backgroundColor: "#FFD700",
        borderColor: "#FFD700",
        borderWidth: 1,
        borderRadius: 4,
        categoryPercentage: 0.6,
        barPercentage: 0.7,
        maxBarThickness: 50,
        stack: "stack1",
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { color: "#333", font: { size: 12 } },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grace: "15%",
        ticks: { color: "#333", font: { size: 12 } },
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          color: "#333",
          font: { size: 13, weight: 500 },
          usePointStyle: true,
          pointStyle: "rect",
          boxWidth: 12,
          boxHeight: 12,
        },
      },
      datalabels: {
        color: "#333",
        font: { size: 11, weight: "bold" },
        anchor: "end",
        align: "top",
        offset: 6,
        formatter: (value) => (value > 0 ? value : ""),
      },
    },
  };

  return (
    <div className="active-strs-doughnut-chart-container">
      <div className="active-str-doughnut-chart-header d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <h4 className="active-str-doughnut-panel-title m-0">Completed STRs by&nbsp;</h4>
          <SortBySelect
            value={selectedCategory}
            handleSelect={handleCategoryChange}
            options={Object.keys(categoryMappings)}
          />
          <SortBySelect
            value={sort}
            handleSelect={handleSelect}
            options={["Last 7 Days", "Last Month", "Last 3 Months", "Custom"]}
          />
        </div>
        <FluentButton
          appearance="none"
          onClick={() => setModalVisible(true)}
          icon={<FluentUiIcon name="ArrowMaximizeRegular" color="#0F6CBD" size={20} />}
        />
      </div>

      <div style={{ height: 400 }}>
        <Bar data={chartData} options={options} plugins={[ChartDataLabels]} />
      </div>

      <Modal show={calendarShow} onHide={handleCalendarClose}>
        <DateRangePicker
          start={customStart}
          setStart={setCustomStart}
          end={customEnd}
          setEnd={setCustomEnd}
        />
      </Modal>

      <Modal
        show={modalVisible}
        onHide={() => setModalVisible(false)}
        dialogClassName="active-tasks-modal-dialog"
      >
        <Modal.Header>
          <div className="d-flex justify-content-between align-items-center w-100">
            <h4 className="active-str-doughnut-panel-title m-0">
              Completed STRs by {selectedCategory}
            </h4>
            <button
              className="active-tasks-exit-modal header-button"
              onClick={() => setModalVisible(false)}
            />
          </div>
        </Modal.Header>
        <Modal.Body>
          <Bar data={chartData} options={options} plugins={[ChartDataLabels]} />
        </Modal.Body>
      </Modal>
    </div>
  );
};

CompletedSTRsBarGraph.propTypes = {
  data: PropTypes.object.isRequired,
  fetchData: PropTypes.func.isRequired,
  customStart: PropTypes.instanceOf(Date),
  customEnd: PropTypes.instanceOf(Date),
  setCustomStart: PropTypes.func.isRequired,
  setCustomEnd: PropTypes.func.isRequired,
};

export default CompletedSTRsBarGraph;